import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../../../../API';
import { girderLabelImageUrl, girderThumbnailUrl } from '../../../../../utils/girderThumbnailUrl';

/** Display size for the list cell (CSS only — fetch uses stock 160×100). */
export const DEFAULT_THUMB_SIZE = 40;
/** Max display size for the hover peek (same stock URL as the list thumb). */
export const DEFAULT_PEEK_SIZE = 360;

function placeholderStyle(size) {
    return {
        display: 'inline-block',
        width: size,
        height: size,
        background: '#f0f0f0',
        borderRadius: 4,
        border: '1px solid #e1e4e8',
        boxSizing: 'border-box',
        flexShrink: 0,
    };
}

/**
 * Small thumb with hover peek (portaled so overflow/virtualization does not clip it).
 * kind: 'thumbnail' (WSI overview) | 'label' (associated label image)
 *
 * List and peek share one stock URL (160×100) so the browser caches a single fetch.
 */
export default function HoverPeekImage({
    itemId,
    kind,
    hasLargeImage,
    label,
    size = DEFAULT_THUMB_SIZE,
    peekSize = DEFAULT_PEEK_SIZE,
}) {
    const [failed, setFailed] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [peekStyle, setPeekStyle] = useState({});
    const wrapperRef = useRef(null);
    const peekRef = useRef(null);

    useEffect(() => {
        setFailed(false);
        setHovered(false);
    }, [itemId, kind]);

    useEffect(() => {
        if (!hovered || !wrapperRef.current || !peekRef.current) return undefined;

        const updatePosition = () => {
            if (!wrapperRef.current || !peekRef.current) return;
            const wrap = wrapperRef.current.getBoundingClientRect();
            const peek = peekRef.current.getBoundingClientRect();
            const spacing = 10;
            let top = wrap.top + (wrap.height - peek.height) / 2;
            let left = wrap.right + spacing;

            if (left + peek.width > window.innerWidth - 8) {
                left = wrap.left - peek.width - spacing;
            }
            if (left < 8) left = 8;
            if (top < 8) top = 8;
            if (top + peek.height > window.innerHeight - 8) {
                top = window.innerHeight - peek.height - 8;
            }

            setPeekStyle({
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                zIndex: 10050,
                pointerEvents: 'none',
            });
        };

        requestAnimationFrame(() => requestAnimationFrame(updatePosition));
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [hovered]);

    if (!itemId || failed) {
        return <span aria-hidden="true" title={label} style={placeholderStyle(size)} />;
    }

    const src = kind === 'label'
        ? girderLabelImageUrl(api, itemId)
        : girderThumbnailUrl(api, itemId);

    const peek = hovered ? (
        <div ref={peekRef} style={peekStyle}>
            <div
                style={{
                    background: '#fff',
                    border: '1px solid #cfd3d7',
                    borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                    padding: 8,
                    maxWidth: peekSize + 24,
                }}
            >
                <div style={{ fontSize: 12, color: '#57606a', marginBottom: 6, fontWeight: 600 }}>
                    {label}
                </div>
                <img
                    src={src}
                    alt=""
                    style={{
                        display: 'block',
                        maxWidth: peekSize,
                        maxHeight: peekSize,
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        background: '#f6f8fa',
                        borderRadius: 4,
                    }}
                />
            </div>
        </div>
    ) : null;

    return (
        <>
            <span
                ref={wrapperRef}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{ display: 'inline-flex', cursor: 'zoom-in' }}
            >
                <img
                    src={src}
                    alt={label}
                    width={size}
                    height={size}
                    loading="lazy"
                    onError={() => setFailed(true)}
                    style={{
                        display: 'block',
                        width: size,
                        height: size,
                        objectFit: 'contain',
                        borderRadius: 4,
                        border: '1px solid #e1e4e8',
                        background: '#f0f0f0',
                        opacity: hasLargeImage === false ? 0.5 : 1,
                    }}
                />
            </span>
            {peek && typeof document !== 'undefined' && createPortal(peek, document.body)}
        </>
    );
}

/** Slide overview + label image thumbs for one row. */
export function SlidePreviewThumbs({
    itemId,
    hasLargeImage,
    size = DEFAULT_THUMB_SIZE,
    peekSize = DEFAULT_PEEK_SIZE,
}) {
    return (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <HoverPeekImage
                itemId={itemId}
                kind="thumbnail"
                hasLargeImage={hasLargeImage}
                label="Slide"
                size={size}
                peekSize={peekSize}
            />
            <HoverPeekImage
                itemId={itemId}
                kind="label"
                hasLargeImage={hasLargeImage}
                label="Label"
                size={size}
                peekSize={peekSize}
            />
        </div>
    );
}
